/**
 * Hook for fetching project initialization hierarchical data:
 * - Scheduling tasks and spaces from Sitedrive
 * - Template procurement packages for the selected template set
 * 
 * This supports the new Plot -> Procurement Package -> Work Section hierarchy.
 */

import { gql, useQuery } from "@apollo/client";

const GET_SCHEDULING_TASKS_FOR_IMPORT = gql`
  query GetSchedulingTasksForImport($projectId: String!, $taskTemplateSetId: String!) {
    schedulingTasksForImport(projectId: $projectId, taskTemplateSetId: $taskTemplateSetId) {
      name
      spaceId
      spaceName
      plannedStart
      plannedEnd
      sectionName
      sectionCode
      schedulingTaskIds
      procurementPackageName
      procurementPackageCode
    }
  }
`;

const GET_SCHEDULING_PP_REFERENCE_TASKS = gql`
  query GetSchedulingPpReferenceTasks($projectId: String!, $taskTemplateSetId: String!) {
    schedulingProcurementPackageReferenceTasks(projectId: $projectId, taskTemplateSetId: $taskTemplateSetId) {
      spaceId
      plannedStart
      plannedEnd
      procurementPackageCode
      procurementPackageName
    }
  }
`;

const GET_SCHEDULING_PROJECT_SPACES = gql`
  query GetSchedulingProjectSpaces($projectId: String!) {
    schedulingProjectSpaces(projectId: $projectId) {
      id
      name
      parentId
      orderNumber
      type
    }
  }
`;

const GET_TEMPLATE_PROCUREMENT_PACKAGES = gql`
  query GetTemplateProcurementPackages($taskTemplateSetId: String!) {
    templateProcurementPackages(filter: { taskTemplateSetId: $taskTemplateSetId }, limit: 10000) {
      items {
        id
        code
        name
      }
    }
  }
`;

const GET_TEMPLATE_TASKS_AVAILABILITY = gql`
  query GetTemplateTasksAvailability($taskTemplateSetId: String!) {
    templateTasks(
      filter: { taskTemplateSetId: $taskTemplateSetId, isTopLevel: true }
      limit: 10000
    ) {
      items {
        id
        name
        templateProcurementPackageId
        templateWorkSectionId
      }
    }
  }
`;

const GET_TEMPLATE_WORK_SECTIONS = gql`
  query GetTemplateWorkSections($taskTemplateSetId: String!) {
    templateWorkSections(filter: { taskTemplateSetId: $taskTemplateSetId }, limit: 10000) {
      items {
        id
        code
        name
        templateProcurementPackageId
      }
    }
  }
`;

export interface SchedulingTaskForImport {
  name: string;
  spaceId: string;
  spaceName: string;
  plannedStart: string;
  plannedEnd: string;
  sectionName: string;
  sectionCode: string;
  schedulingTaskIds: string[];
  procurementPackageName?: string | null;
  procurementPackageCode?: string | null;
}

export interface SchedulingSpace {
  id: string;
  name: string;
  parentId?: string | null;
  orderNumber?: number | null;
  type?: string | null;
}

export interface TemplateWorkSection {
  id: string;
  code: string;
  name?: string | null;
  templateProcurementPackageId?: string | null;
}

export interface TemplateProcurementPackage {
  id: string;
  code: string;
  name?: string | null;
}

export interface TemplateTaskAvailability {
  id: string;
  name: string;
  templateProcurementPackageId?: string | null;
  templateWorkSectionId?: string | null;
}

/**
 * Availability look-ups derived from the selected template's tasks.
 *
 * - `availableAtPp.get(templatePpId)` → union of task names directly attached
 *   to the PP *and* to any work section owned by that PP.  Used to enable the
 *   PP-level checkboxes (Suunnitelmiin perehtyminen, Aliurakkasopimukseen
 *   perehtyminen, Aloitusedellytykset, Taloudellinen loppuselvitys) — the
 *   backend stores several of these at the work-section level even though the
 *   UI surfaces them on the PP row.
 * - `availableAtWs.get(templateWsId)` → task names attached to that work
 *   section.  Used to enable the space-level checkboxes (Osakohteen tarkastus,
 *   Malliasennustarkastus, Mestan vastaanotto).
 */
export interface TemplateTaskAvailabilityMaps {
  availableAtPp: Map<string, Set<string>>;
  availableAtWs: Map<string, Set<string>>;
}

// New hierarchical state types
export interface SpaceState {
  schedulingSpaceId: string;
  schedulingTaskIds: string[];
  plannedStart: string;
  plannedEnd: string;
  includeOsakohteenTarkastus: boolean;
  includeMalliasennustarkastus: boolean;
  includeMestanVastaanotto: boolean;
  includeTyovaiheenvastaanotto: boolean;
  includeLiitedokumentit: boolean;
}

export interface SpaceResponsibleState {
  /** DB location id, needed by the project-viewer to upsert a section-location.
   *  null in the init / mini-init flows, where locations may not exist yet and
   *  assignments are persisted server-side by scheduling_space_id instead. */
  locationId: string | null;
  /** Staged responsible user id; null = inherit from the section. */
  responsibleUserId: string | null;
  /** Existing section-location id, or null when none exists yet. */
  sectionLocationId: string | null;
  sectionLocationVersion: number | null;
  /** Responsible at load time, for dirty comparison. */
  originalResponsibleUserId: string | null;
}

export interface WorkSectionState {
  sectionCode: string;
  sectionName: string;
  templateWorkSectionId?: string;
  spaces: SpaceState[];
  plannedStart?: string | null;
  plannedEnd?: string | null;
  /** Per-WS inspector (Tarkastaja). null = inherit from the parent PP's
   *  responsibleUserId. */
  inspectorUserId: string | null;
  /** Staged section-location responsibles, keyed by scheduling-space id.
   *  Seeded from existing section-locations; ancestor rows without a
   *  section-location are absent until the user assigns one. */
  spaceResponsibles: Record<string, SpaceResponsibleState>;
}

export interface ProcurementPackageState {
  templateProcurementPackageId: string;
  code: string;
  name?: string | null;
  responsibleUserId: string | null;
  includeSuunnitelmiin: boolean;
  includeAliurakka: boolean;
  includeAloitusedellytykset: boolean;
  includeTaloudellinenLoppuselvitys: boolean;
  workSections: WorkSectionState[];
  plannedStart?: string | null;
  plannedEnd?: string | null;
  /** True when this PP already exists in the project DB. Used by mini-init
   *  to mark cards as read-only — PP-level controls are disabled, only the
   *  newly-discovered work sections under the PP are editable. */
  isExisting?: boolean;
}

export interface PlotState {
  name: string;
  locationSchedulingSpaceId: string | null;
  procurementPackages: ProcurementPackageState[];
}

export function useProjectInitData(
  schedulingProjectId: string | null,
  taskTemplateSetId: string | null
) {
  // Fetch scheduling data from Sitedrive
  const {
    data: tasksData,
    loading: tasksLoading,
    error: tasksError
  } = useQuery(
    GET_SCHEDULING_TASKS_FOR_IMPORT,
    {
      variables: { projectId: schedulingProjectId!, taskTemplateSetId: taskTemplateSetId! },
      skip: !schedulingProjectId || !taskTemplateSetId,
      fetchPolicy: "cache-and-network",
      onError: (error) => {
        console.error("[useProjectInitData] Error loading scheduling tasks:", error);
      },
    }
  );

  const {
    data: ppRefData,
    loading: ppRefLoading,
    error: ppRefError,
  } = useQuery(GET_SCHEDULING_PP_REFERENCE_TASKS, {
    variables: { projectId: schedulingProjectId!, taskTemplateSetId: taskTemplateSetId! },
    skip: !schedulingProjectId || !taskTemplateSetId,
    fetchPolicy: "cache-and-network",
    onError: (error) => {
      console.error("[useProjectInitData] Error loading PP reference tasks:", error);
    },
  });

  const {
    data: spacesData,
    loading: spacesLoading,
    error: spacesError
  } = useQuery(
    GET_SCHEDULING_PROJECT_SPACES,
    {
      variables: { projectId: schedulingProjectId! },
      skip: !schedulingProjectId,
      fetchPolicy: "cache-and-network",
      onError: (error) => {
        console.error("[useProjectInitData] Error loading scheduling spaces:", error);
      },
    }
  );

  // Fetch template procurement packages
  const { 
    data: packagesData, 
    loading: packagesLoading, 
    error: packagesError 
  } = useQuery(
    GET_TEMPLATE_PROCUREMENT_PACKAGES,
    {
      variables: { taskTemplateSetId: taskTemplateSetId! },
      skip: !taskTemplateSetId,
      fetchPolicy: "cache-and-network",
      onError: (error) => {
        console.error("[useProjectInitData] Error loading procurement packages:", error);
      },
    }
  );

  // Fetch template task availability (for disabling checkboxes whose task
  // does not exist in the selected template set)
  const {
    data: templateTasksData,
    loading: templateTasksLoading,
    error: templateTasksError,
  } = useQuery(GET_TEMPLATE_TASKS_AVAILABILITY, {
    variables: { taskTemplateSetId: taskTemplateSetId! },
    skip: !taskTemplateSetId,
    fetchPolicy: "cache-and-network",
    onError: (error) => {
      console.error(
        "[useProjectInitData] Error loading template task availability:",
        error,
      );
    },
  });

  // Fetch template work sections
  const {
    data: workSectionsData,
    loading: workSectionsLoading,
    error: workSectionsError,
  } = useQuery(
    GET_TEMPLATE_WORK_SECTIONS,
    {
      variables: { taskTemplateSetId: taskTemplateSetId! },
      skip: !taskTemplateSetId,
      fetchPolicy: "cache-and-network",
      onError: (error) => {
        console.error("[useProjectInitData] Error loading template work sections:", error);
      },
    }
  );

  // Combine errors for better debugging
  const combinedError =
    tasksError || spacesError || packagesError || workSectionsError || templateTasksError || ppRefError;
  
  // Create a more descriptive error message
  let errorMessage = combinedError?.message;
  if (tasksError) {
    errorMessage = `Sitedrive-tehtävät: ${tasksError.message}`;
  } else if (spacesError) {
    errorMessage = `Sitedrive-tilat: ${spacesError.message}`;
  } else if (packagesError) {
    errorMessage = `Hankintapaketit: ${packagesError.message}`;
  } else if (workSectionsError) {
    errorMessage = `Työvaihepohjat: ${workSectionsError.message}`;
  }
  
  const descriptiveError = combinedError ? {
    ...combinedError,
    message: errorMessage || "Tuntematon virhe",
  } : null;
  
  if (combinedError) {
    console.error("[useProjectInitData] Combined error:", {
      tasksError: tasksError?.message,
      spacesError: spacesError?.message,
      packagesError: packagesError?.message,
      workSectionsError: workSectionsError?.message,
      schedulingProjectId,
      taskTemplateSetId,
    });
  }

  return {
    tasks: (tasksData?.schedulingTasksForImport ?? [])
      .filter((task: SchedulingTaskForImport) =>
        task.procurementPackageName != null && task.procurementPackageCode != null
      ) as SchedulingTaskForImport[],
    procurementPackageReferenceTasks:
      (ppRefData?.schedulingProcurementPackageReferenceTasks ?? []) as SchedulingTaskForImport[],
    spaces: (spacesData?.schedulingProjectSpaces ?? []) as SchedulingSpace[],
    procurementPackages: (packagesData?.templateProcurementPackages?.items ?? []) as TemplateProcurementPackage[],
    templateWorkSections: (workSectionsData?.templateWorkSections?.items ?? []) as TemplateWorkSection[],
    templateTaskAvailability: buildAvailabilityMaps(
      (templateTasksData?.templateTasks?.items ?? []) as TemplateTaskAvailability[],
      (workSectionsData?.templateWorkSections?.items ?? []) as TemplateWorkSection[],
    ),
    loading:
      tasksLoading ||
      spacesLoading ||
      packagesLoading ||
      workSectionsLoading ||
      templateTasksLoading ||
      ppRefLoading,
    error: descriptiveError,
  };
}

function buildAvailabilityMaps(
  items: TemplateTaskAvailability[],
  workSections: TemplateWorkSection[],
): TemplateTaskAvailabilityMaps {
  const availableAtWs = new Map<string, Set<string>>();
  const availableAtPp = new Map<string, Set<string>>();

  const ensure = (map: Map<string, Set<string>>, key: string): Set<string> => {
    let set = map.get(key);
    if (!set) {
      set = new Set<string>();
      map.set(key, set);
    }
    return set;
  };

  for (const item of items) {
    if (item.templateProcurementPackageId) {
      ensure(availableAtPp, item.templateProcurementPackageId).add(item.name);
    }
    if (item.templateWorkSectionId) {
      ensure(availableAtWs, item.templateWorkSectionId).add(item.name);
    }
  }

  // Fold WS tasks into their owning PP so PP-level checkboxes reflect the
  // backend's habit of storing these tasks at the work-section level.
  for (const ws of workSections) {
    if (!ws.templateProcurementPackageId) continue;
    const wsTasks = availableAtWs.get(ws.id);
    if (!wsTasks) continue;
    const ppSet = ensure(availableAtPp, ws.templateProcurementPackageId);
    for (const name of wsTasks) ppSet.add(name);
  }

  return { availableAtPp, availableAtWs };
}
